import * as React from "react";
import { useTheme } from "@mui/material";
import { useApi } from "../../containers/ApiContainer";
import { useAutocompleteResults } from "../../containers/AutocompleteResultsContainer";
import { useLoadingProgress } from "../../containers/LoadingProgressContainer";
import { useUserPreferences } from "../../containers/UserPreferencesContainer";
import { useAircraft } from "./hooks/useAircraft";
import { useAutocompleteTenant } from "./hooks/useAutocompleteTenant";
import { useDimensions } from "./hooks/useDimensions";
import { useGetClickedEntity } from "./hooks/useGetClickedEntity";
import { useGetIndexOfGhostAircraftHoveringOver } from "./hooks/useGetIndexOfGhostAircraftHoveringOver";
import { useIsInLayout } from "./hooks/useIsInLayout";
import { useMovableObstacles } from "./hooks/useMovableObstacles";
import { useOnClick } from "./hooks/useOnClick";
import { useOnContextMenu } from "./hooks/useOnContextMenu";
import { usePolygons } from "./hooks/usePolygons";
import { useUnselect } from "./hooks/useUnselect";
import { LayoutBackground } from "./LayoutBackground";
import { CollisionOverlay } from "./widgets/CollisionOverlay";
import { Door } from "./widgets/Door";
import { GhostAircraft } from "./widgets/GhostAircraft";
import { Obstacle } from "./widgets/Obstacle";

import {
  AutocompletePosition,
  Entity,
  Hangar,
  Location,
  MovableObstacle,
  Position,
  Preference,
  Ramp,
  Stack,
  Tenant,
} from "../../types";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { DraggingScrollContainer } from "../DraggingScrollContainer/DraggingScrollContainer";

export type LayoutDisplayOptions = {
  hideUnplaced?: boolean;
  selectable?: boolean;
  readOnly?: boolean;
  printable?: boolean;
  dashColor?: string;
  backgroundColor?: string;
  disableDragProtection?: boolean;
  borderColor?: string;
  hideTailNumbers?: boolean;
  hideNotes?: boolean;
  frozen?: boolean;
  disableGrabScroll?: boolean;
};

export type MoveEntityEvent = {
  direction: string;
  entity: Entity<Tenant | MovableObstacle>;
  previousPosition: Position;
  locationId: string;
};

type Props = {
  stack: Stack;
  setStack: (stack: Stack) => void;
  width?: number;
  height?: number;
  location: Location<Hangar | Ramp>;
  autocomplete?: (tenant: Tenant) => Promise<AutocompletePosition[]>;
  options: LayoutDisplayOptions;
  zoomLevel?: number;
  // Optional callback for scheduled view updates
  onScheduledUpdate?: () => void;
  // Optional scheduled view props
  scheduledViewProps?: {
    currentTime: Date;
    heatmapPlacements?: any[];
    [key: string]: any;
  };
  onIncrementalPark?: (aircraftId: string) => void;
};

export type RightClick = {
  entity_id: string;
  mouseX: number;
  mouseY: number;
};

export const HANGAR_BACKGROUND_COLORS = {
  reference: "#F4D2CD",
  testing: "#23967F",
};

export const Layout: React.FC<Props> = ({
  stack,
  setStack,
  location,
  autocomplete: originalAutocomplete,
  options,
  zoomLevel,
  onScheduledUpdate,
  scheduledViewProps,
  onIncrementalPark,
  ...props
}) => {
  const autocomplete =
    originalAutocomplete ?? (async () => [] as AutocompletePosition[]);
  const { setShow: setShowLoadingProgress } = useLoadingProgress();
  const hideUnplaced = options?.hideUnplaced ?? false;
  const readOnly = options?.readOnly ?? false;
  const frozen = options?.frozen ?? false;
  const printable = options?.printable ?? false;
  const spacingBufferRef = React.useRef<HTMLDivElement>();
  const spacingBufferOverlayElement = spacingBufferRef.current;
  const [blockGhostHighlight, setBlockGhostHightlight] =
    React.useState<boolean>(false);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const bboxRef = React.useRef<React.ReactNode>();
  const [ghostMouseOverIndex, setGhostMouseOverIndex] =
    React.useState<number>();
  const { loading: loadingUserPreferences, preferences } = useUserPreferences();
  const [rightClick, setRightClick] = React.useState<RightClick>(null);
  const { autocompleteResults, setAutocompleteResults } =
    useAutocompleteResults();
  const { postgrest } = useApi();

  const [deleteConfirmation, setDeleteConfirmation] = React.useState<{
    type: 'aircraft' | 'obstacle';
    tenant?: Tenant;
    obstacle?: MovableObstacle;
  } | null>(null);

  // Store stack in ref to prevent flicker
  const stackRef = React.useRef(stack);
  stackRef.current = stack;  // Direct update during render (safe for refs)

  const { width, height, feetToPixels } = useDimensions({
    location,
    height: props.height,
    width: props.width,
  });

  const { polygons } = usePolygons(
    {
      stack,
      width,
      feetToPixels,
      isHoldingArea: location.id === "holding-area",
    },
    [width, stack.tenants, stack.movableObstacles, feetToPixels]
  );

  // TODO: make ghost and moving aircraft use hook
  const { polygons: ghostPolygons } = usePolygons(
    {
      stack: {
        ...stack,
        tenants: autocompleteResults.suggestedPositions
          .filter((sp) => sp.hangar_id === location.id)
          .map((position) => ({
            ...autocompleteResults.tenant,
            position: {
              ...autocompleteResults.tenant?.position,
              ...position,
            },
          })),
        movableObstacles: [],
      },
      width,
      feetToPixels,
    },
    [width, autocompleteResults]
  );


  // This hook is responsible for unselecting an aircraft if the user taps
  // anywhere on the page except for a plane or the aircraft list
  useUnselect(
    {
      stack,
      setStack,
    },
    [stack, setStack]
  );

  const isInLayout = useIsInLayout({ location, stack, polygons }, [
    stack.tenants,
    stack.movableObstacles,
    polygons,
  ]);

  const getClickedEntity = useGetClickedEntity(
    {
      bboxRef,
      stack,
      polygons,
      feetToPixels,
    },
    [stack, polygons, feetToPixels]
  );

  const getIndexOfGhostAircraftHoveringOver =
    useGetIndexOfGhostAircraftHoveringOver(
      bboxRef,
      stack,
      ghostPolygons,
      feetToPixels,
      width
    );

  /**
   * Autocomplete the aircraft position
   */
  const autocompleteTenant = useAutocompleteTenant(
    {
      hangarId: location.id,
      enabled: Boolean(originalAutocomplete),
      stack,
      preferences,
      autocomplete: async (tenant: Tenant) => await autocomplete(tenant),
      isInLayout,
      autocompleteResults,
      setAutocompleteResults,
    },
    [stack, preferences, autocompleteResults]
    // Note: autocompleteResults MUST be in deps so the callback can see updated state
    // and early-return (lines 54-61 in useAutocompleteTenant.ts) to prevent infinite loops
  );

  // Store latest autocompleteTenant in a ref to prevent infinite loops
  const autocompleteTenantRef = React.useRef(autocompleteTenant);
  React.useEffect(() => {
    autocompleteTenantRef.current = autocompleteTenant;
  }, [autocompleteTenant]);

  // Run autocomplete when an aircraft is selected (only when selection actually changes)
  const selectedTenantIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const selectedTenant = stack.tenants.find((t) => t.selected);
    const selectedId = selectedTenant?.id || null;

    // Only run autocomplete if the selected tenant ID actually changed
    // (prevents infinite loop when stack.tenants array is recreated with same content)
    if (selectedTenantIdRef.current === selectedId) {
      return;
    }

    selectedTenantIdRef.current = selectedId;

    if (selectedTenant) {
      autocompleteTenantRef.current(selectedTenant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stack.tenants]);
  // Note: autocompleteTenant intentionally NOT in deps - using ref to prevent infinite loop
  // (autocompleteResults changes → autocompleteTenant recreated → would trigger this effect again)

  // Delete key shortcut
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      if (readOnly || frozen) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        const currentStack = stackRef.current;

        const selectedTenant = currentStack.tenants.find(t => t.selected);
        if (selectedTenant) {
          e.preventDefault();
          setDeleteConfirmation({ type: 'aircraft', tenant: selectedTenant });
          return;
        }

        const selectedObstacle = currentStack.movableObstacles.find(mo => mo.selected);
        if (selectedObstacle) {
          e.preventDefault();
          setDeleteConfirmation({ type: 'obstacle', obstacle: selectedObstacle });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [readOnly, frozen]);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!deleteConfirmation) return;
    const currentStack = stackRef.current;  // Read from ref, not closure

    if (deleteConfirmation.type === 'aircraft' && deleteConfirmation.tenant) {
      const tenant = deleteConfirmation.tenant;
      setStack({
        ...currentStack,
        tenants: currentStack.tenants.filter(t => t.placement_id !== tenant.placement_id),
      });
      if (currentStack.name === 'reference') {
        await postgrest
          .from('tenant')
          .update({ type: 'transient' })
          .eq('id', tenant.id);
      }

      // Trigger refresh for scheduled view
      if (onScheduledUpdate) {
        onScheduledUpdate();
      }
    } else if (deleteConfirmation.type === 'obstacle' && deleteConfirmation.obstacle) {
      setStack({
        ...currentStack,
        movableObstacles: currentStack.movableObstacles.filter(
          mo => mo.entity_id !== deleteConfirmation.obstacle.entity_id
        ),
      });

      // Trigger refresh for scheduled view
      if (onScheduledUpdate) {
        onScheduledUpdate();
      }
    }
    setDeleteConfirmation(null);
  }, [deleteConfirmation, setStack, postgrest, onScheduledUpdate]);
  // Note: stack removed from dependencies - using stackRef prevents callback recreation on every drag

  const onClick = useOnClick(
    {
      location,
      stack,
      setStack,
      autocompleteResults,
      setAutocompleteResults,
      rightClick,
      getClickedEntity,
      getIndexOfGhostAircraftHoveringOver,
    },
    [stack, setStack, ghostPolygons, autocompleteResults, rightClick, getClickedEntity, getIndexOfGhostAircraftHoveringOver]
  );

  const onMouseMove = React.useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (blockGhostHighlight) {
        return;
      }
      const index = getIndexOfGhostAircraftHoveringOver(evt);
      setGhostMouseOverIndex(index);
    },
    [stack, ghostPolygons, getIndexOfGhostAircraftHoveringOver, blockGhostHighlight]
  );

  const onContextMenu = useOnContextMenu(
    {
      stack,
      getClickedEntity,
      setRightClick,
    },
    [stack, getClickedEntity]
  );
  const Aircraft = useAircraft(
    {
      location,
      stack,
      setStack,
      hideUnplaced,
      isInLayout,
      preferences,
      printable,
      rightClick,
      setRightClick,
      readOnly,
      frozen,
      width,
      height,
      feetToPixels,
      spacingBufferOverlayElement,
      options,
      onScheduledUpdate,
      scheduledViewProps,
      onIncrementalPark,
    },
    [
      location,
      stack,
      setStack,
      readOnly,
      height,
      feetToPixels,
      preferences,
      rightClick,
      setRightClick,
      spacingBufferOverlayElement,
      options,
    ]
  );

  const MovableObstacles = useMovableObstacles(
    {
      location,
      stack,
      setStack,
      preferences,
      rightClick,
      setRightClick,
      readOnly,
      height,
      feetToPixels,
      spacingBufferOverlayElement,
      options,
    },
    [
      stack,
      setStack,
      readOnly,
      height,
      feetToPixels,
      preferences,
      rightClick,
      setRightClick,
      spacingBufferOverlayElement,
      options,
    ]
  );

  React.useEffect(() => {
    setShowLoadingProgress(autocompleteResults?.loading);
  }, [autocompleteResults?.loading]);

  React.useEffect(() => {
    return () => {
      // Clear autocomplete results and hide loading progress when unmounting
      setAutocompleteResults({
        id: null,
        hash: null,
        loading: false,
        tenant: null,
        orientation: null,
        suggestedPositions: [],
      });
      setShowLoadingProgress(false);
    };
  }, []);

  // if a locked aircraft is moved outside the hangar, then unlock it
  React.useEffect(() => {
    for (const tenant of stack.tenants) {
      if (tenant.position.preferences.includes(Preference.LOCKED)) {
        if (!isInLayout(tenant)) {
          setStack({
            ...stack,
            tenants: stack.tenants.map((t) => {
              if (t.id === tenant.id) {
                return {
                  ...t,
                  position: {
                    ...t.position,
                    preferences: t.position.preferences.filter(
                      (p) => p !== Preference.LOCKED
                    ),
                  },
                };
              }
              return t;
            }),
          });
        }
      }
    }
  }, [stack.tenants]);

  const onMouseDown = () => setBlockGhostHightlight(true);
  const onMouseUp = () => setBlockGhostHightlight(false);

  // wait for user prefs...
  if (loadingUserPreferences) {
    return <div />;
  }

  const backgroundColor =
    options?.backgroundColor ??
    (stack.isReference
      ? HANGAR_BACKGROUND_COLORS.reference
      : isDarkMode
        ? theme.palette.background.default
        : "#aab1c7");
  const dashColor =
    options?.dashColor ??
    (stack.isReference
      ? "#f8f8f8"
      : isDarkMode
        ? "#5a6775"
        : "#5474a4");
  const borderColor = options?.borderColor ??
    (isDarkMode ? theme.palette.divider : undefined);

  const autocompleteResultsForThisLayout = {
    ...autocompleteResults,
    suggestedPositions: autocompleteResults.suggestedPositions.filter(
      (sp) => sp.hangar_id === location.id
    ),
  };

  return (
    <DraggingScrollContainer
      disabled={readOnly || options?.disableGrabScroll}
      feetToPixels={feetToPixels}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div
        data-location-id={location.id}
        data-layout={stack.id}
        style={{ display: "flex" }}
      >
        <LayoutBackground
          ref={bboxRef}
          location={location}
          preferences={preferences}
          selectable={false}
          width={width}
          height={height}
          feetToPixels={feetToPixels}
          zoomLevel={zoomLevel}
          backgroundColor={backgroundColor}
          dashColor={dashColor}
          borderColor={borderColor}
          printable={printable}
          onClick={onClick}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onMouseEnter={() => null}
          onContextMenu={onContextMenu}
          heatmapPlacements={scheduledViewProps?.heatmapPlacements}
          heatmapSelectedDate={scheduledViewProps?.currentTime}
        >
          {!readOnly && preferences.showOverlaps && (
            <CollisionOverlay location={location} polygons={polygons ?? []} />
          )}
          {location.type === "hangar" &&
            Boolean((location as Hangar).left_door) && (
              <Door
                feetToPixels={feetToPixels}
                position="left"
                width={(location as Hangar).left_door}
              />
            )}
          {location.type === "hangar" &&
            Boolean((location as Hangar).right_door) && (
              <Door
                feetToPixels={feetToPixels}
                position="right"
                width={(location as Hangar).right_door}
              />
            )}

          {location.type === "hangar" &&
            location.obstacles.map((obstacle) => (
              <React.Fragment key={`obstacle-${obstacle.id}`}>
                <Obstacle {...obstacle} feetToPixels={feetToPixels} />
              </React.Fragment>
            ))}
          {location.type === "hangar" &&
            location.garageDoors.map((garageDoor) => (
              <React.Fragment key={`garage-door-${garageDoor.id}`}>
                <Door
                  type="garage"
                  feetToPixels={feetToPixels}
                  position={garageDoor.wall}
                  offset={
                    garageDoor.wall === "back" ? garageDoor.x : garageDoor.y
                  }
                  width={garageDoor.width}
                />
              </React.Fragment>
            ))}
          <div ref={spacingBufferRef} />
          {/**
           * aircraft is a memo so that it doesn't update during a drag event. this is because we only store state
           * at the end of a drag event
           */}
          {Aircraft}
          {MovableObstacles}
          {Boolean(preferences?.enableCoPilot) && (
            <GhostAircraft
              location={location}
              stack={stack}
              polygons={polygons}
              autocompleteResults={autocompleteResultsForThisLayout}
              mouseOverIndex={ghostMouseOverIndex}
              feetToPixels={feetToPixels}
              height={height}
            />
          )}
        </LayoutBackground>
      </div>
      {deleteConfirmation && (
        <ConfirmationDialog
          title="Please Confirm"
          text={
            deleteConfirmation.type === 'aircraft'
              ? stack.name === 'reference'
                ? 'Making this Tenant into a Transient means this aircraft is no longer based in your hangars. It will remain on your transient list of aircraft.'
                : `This aircraft will be removed from this ${location.type}.`
              : 'This movable obstacle will be removed.'
          }
          okText={deleteConfirmation.type === 'aircraft' ? 'Yes, remove this aircraft' : 'Yes, remove this obstacle'}
          onOk={handleConfirmDelete}
          onClose={() => setDeleteConfirmation(null)}
          onCancel={() => setDeleteConfirmation(null)}
        />
      )}
    </DraggingScrollContainer>
  );
};
